import React, { Fragment, useCallback, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { useRequest } from 'ahooks';

import { ClearIcon, ExclamationTriangleIcon, LoadingIndicator } from './Icons';

/**
 * Manages search state and API calls.
 *
 * @param {function(string): Promise<Array<unknown>>} fetchOptionsFunction - Function that takes query and returns promise
 * @param {number} debounceWait - Debounce delay in milliseconds
 */
const useSearch = (fetchOptionsFunction, debounceWait = 500) => {
  const [query, setQuery] = useState('');
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');

  const hasQuery = query.trim().length >= 2;
  const dirty = query !== lastSearchedQuery;

  const {
    data: results = /** @type {Array<unknown>} */ ([]),
    loading,
    mutate,
  } = useRequest(() => fetchOptionsFunction(query), {
    refreshDeps: [query],
    debounceWait,
    ready: hasQuery,
    onFinally: () => setLastSearchedQuery(query),
  });

  const resetSearch = useCallback(
    (clearSearchResults = false) => {
      setQuery('');
      setLastSearchedQuery('');

      if (clearSearchResults) {
        mutate([]);
      }
    },
    [mutate],
  );

  const busy = hasQuery && (dirty || loading);
  const searched = lastSearchedQuery.length > 0;

  return {
    setQuery,
    results,
    busy,
    searched,
    resetSearch,
  };
};

/**
 * Error icon component for input validation states
 */
export const ErrorIcon = () => {
  return (
    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
      <ExclamationTriangleIcon />
    </div>
  );
};

/**
 * Clear button component for clearing input values
 */
export const ClearButton = ({ onClear }) => {
  return (
    <Combobox.Button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClear();
      }}
      className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 cursor-pointer"
      title="Clear input"
    >
      <ClearIcon />
    </Combobox.Button>
  );
};

export const SearchResults = ({
  results = /** @type {Array<unknown>} */ ([]),
  loading = false,
  searched = false,
  getOptionKey = (val) => val,
  getOptionLabel = (val) => val,
  emptyText = 'No matches found',
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-70">
        <span>Searching...</span>
        <LoadingIndicator />
      </div>
    );
  }

  // Only show empty message if we've actually searched and got no results
  if (searched && (!Array.isArray(results) || results.length === 0)) {
    return <div className="px-4 py-2 text-sm text-gray-70">{emptyText}</div>;
  }

  // If we haven't searched yet, don't render anything
  if (!searched || !Array.isArray(results) || results.length === 0) {
    return null;
  }

  return (
    <>
      {results.map((result, idx) => {
        const key = getOptionKey(result) || idx;

        const base = 'relative py-2 px-4 text-gray-90 truncate cursor-pointer';
        const selectedCls = 'bg-deep-blue-10 text-white font-medium';
        const hoveredCls = 'bg-blue-05';

        return (
          <Combobox.Option
            key={key}
            value={result}
            className={({ active: hovered, selected }) =>
              [
                base,
                selected ? selectedCls : '',
                hovered && !selected ? hoveredCls : '',
              ].join(' ')
            }
          >
            {getOptionLabel(result)}
          </Combobox.Option>
        );
      })}
    </>
  );
};

export const SearchCombobox = React.forwardRef(
  /**
   * @param {any} props
   * @param {React.Ref<HTMLInputElement>} ref
   */
  (props, ref) => {
    const {
      value,
      onChange,
      label = '',
      placeholder = 'Search',
      disabled = false,
      debounceWait = 500,
      emptyText = 'No matches found',
      showError = false,
      fetchOptionsFunction,
      onFocus,
      autoHighlightFirstMatch = false,
      CustomOptions,
      getOptionLabel,
      getOptionKey,
      getInputDisplayValue = getOptionLabel,
      customOnChange, // Allow custom onChange logic
      name, // For react-hook-form compatibility
    } = props;
    /** @type {React.RefObject<HTMLButtonElement>} */
    const openOptionsButtonRef = useRef(null);

    const { setQuery, results, busy, searched, resetSearch } = useSearch(
      fetchOptionsFunction,
      debounceWait,
    );
    const showClear = !showError && (value || busy);

    const handleChange = async (opt) => {
      try {
        // Use custom onChange if provided, otherwise use standard onChange
        if (customOnChange) {
          await customOnChange(opt);
        } else {
          onChange?.(opt || null);
        }
      } catch (error) {
        console.error('SearchCombobox onChange error:', error);
      }
    };

    const handleClear = () => {
      resetSearch(true);
      onChange?.(null);
    };

    const handleFocus = () => {
      onFocus?.(openOptionsButtonRef);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        // If busy searching, prevent closing the dropdown
        if (busy) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      if (e.key === 'Escape') {
        handleClear();
      }
    };

    return (
      <Combobox value={value} onChange={handleChange} disabled={disabled}>
        {label ? (
          <Combobox.Label className="mb-1 block text-sm font-medium text-gray-90">
            {label}
          </Combobox.Label>
        ) : null}

        <div className="relative">
          <Combobox.Input
            className={`w-full ${showError || showClear ? 'pr-10' : ''}`}
            placeholder={placeholder}
            displayValue={getInputDisplayValue || getOptionLabel}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            aria-invalid={showError}
            aria-busy={busy}
            autoFocus={false}
            name={name}
            ref={ref}
          />

          {showError ? (
            <ErrorIcon />
          ) : showClear ? (
            <ClearButton onClear={handleClear} />
          ) : null}

          {/* Hidden button to programmatically open the combobox */}
          <Combobox.Button ref={openOptionsButtonRef} className="hidden" />

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => {
              // Only reset search if a value was selected
              // If no value and there's a query, keep it so user can continue searching
              if (value) {
                resetSearch(true);
              }
            }}
          >
            <Combobox.Options
              className={[
                'absolute top-full left-0 z-9999 translate-y-1 max-h-dvh overflow-y-auto ' +
                  'rounded-md border border-gray-200 bg-white shadow-lg ' +
                  'w-auto min-w-[20rem] max-w-160',
              ].join(' ')}
            >
              {/* Empty option to prevent auto-focusing on first search result */}
              {!autoHighlightFirstMatch && (
                <Combobox.Option value={null} className="hidden" />
              )}

              <SearchResults
                results={results}
                loading={busy}
                searched={searched}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                emptyText={emptyText}
              />

              {CustomOptions && <CustomOptions />}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    );
  },
);
